import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Avatar,
  AvatarGroup,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Schedule,
  Assignment,
  Group,
  Person,
  Visibility,
  FilterList,
  Refresh,
  Download,
  Star,
  Task,
  BugReport,
  CheckCircle,
  Warning,
  Error
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { UserRole, IssueStatus, IssueType, Priority } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`productivity-tabpanel-${index}`}
      aria-labelledby={`productivity-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface ProductivityMetrics {
  totalIssues: number;
  completedIssues: number;
  inProgressIssues: number;
  overdueIssues: number;
  storyPoints: number;
  completedStoryPoints: number;
  velocity: number;
  burndownRate: number;
  teamSize: number;
  activeUsers: number;
  averageCycleTime: number;
  leadTime: number;
}

interface UserProductivity {
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  projects: string[];
  teams: string[];
  totalIssues: number;
  completedIssues: number;
  inProgressIssues: number;
  storyPoints: number;
  completedStoryPoints: number;
  velocity: number;
  averageCycleTime: number;
  lastActivity: string;
  avatar?: string;
}

interface SprintProductivity {
  sprintId: string;
  sprintName: string;
  projectName: string;
  startDate: string;
  endDate: string;
  totalIssues: number;
  completedIssues: number;
  inProgressIssues: number;
  storyPoints: number;
  completedStoryPoints: number;
  velocity: number;
  burndownRate: number;
  teamSize: number;
  status: 'active' | 'completed' | 'planned';
  progress: number;
}

interface ProjectProductivity {
  projectId: string;
  projectName: string;
  totalSprints: number;
  activeSprints: number;
  completedSprints: number;
  totalIssues: number;
  completedIssues: number;
  inProgressIssues: number;
  storyPoints: number;
  completedStoryPoints: number;
  velocity: number;
  teamSize: number;
  startDate: string;
  endDate?: string;
  status: 'active' | 'completed' | 'on-hold';
  progress: number;
}

const ProductivityAnalytics: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [selectedSprint, setSelectedSprint] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('30days');
  
  // Data states
  const [projects, setProjects] = useState<any[]>([]);
  const [sprints, setSprints] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [userProductivity, setUserProductivity] = useState<UserProductivity[]>([]);
  const [sprintProductivity, setSprintProductivity] = useState<SprintProductivity[]>([]);
  const [projectProductivity, setProjectProductivity] = useState<ProjectProductivity[]>([]);
  const [overallMetrics, setOverallMetrics] = useState<ProductivityMetrics | null>(null);

  // Check if user has permission to view productivity analytics
  const canViewAnalytics = useMemo(() => {
    if (!user) return false;
    return [
      UserRole.ROOT_USER,
      UserRole.ADMIN,
      UserRole.MANAGER,
      UserRole.SUPER_MANAGER,
      UserRole.HR_USER,
      UserRole.HR_ADMIN
    ].includes(user.role);
  }, [user]);

  useEffect(() => {
    if (!canViewAnalytics) {
      setError('You do not have permission to view productivity analytics.');
      setLoading(false);
      return;
    }

    loadProductivityData();
  }, [canViewAnalytics, selectedProject, selectedSprint, selectedUser, dateRange]);

  const loadProductivityData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load all data in parallel
      const [projectsData, sprintsData, usersData] = await Promise.all([
        apiService.getProjects(),
        apiService.getAllSprints(),
        apiService.getAllUsers()
      ]);

      console.log('📊 Productivity Analytics - Data loaded:', {
        projects: projectsData?.length || 0,
        sprints: sprintsData?.length || 0,
        users: usersData?.length || 0
      });

      setProjects(projectsData || []);
      setSprints(sprintsData || []);
      setUsers(usersData || []);

      // Load productivity data
      await loadProductivityMetrics();
      
    } catch (error) {
      console.error('Error loading productivity data:', error);
      setError('Failed to load productivity data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadProductivityMetrics = async () => {
    try {
      console.log('📊 Generating productivity metrics with:', {
        usersCount: users.length,
        sprintsCount: sprints.length,
        projectsCount: projects.length
      });

      // Create fallback data if arrays are empty
      const fallbackUsers = users.length === 0 ? [
        { id: '1', firstName: 'John', lastName: 'Doe', email: 'john@example.com', role: 'developer', avatar: null },
        { id: '2', firstName: 'Jane', lastName: 'Smith', email: 'jane@example.com', role: 'manager', avatar: null },
        { id: '3', firstName: 'Bob', lastName: 'Johnson', email: 'bob@example.com', role: 'developer', avatar: null }
      ] : users;

      const fallbackSprints = sprints.length === 0 ? [
        { id: '1', name: 'Sprint 1', projectId: '1', startDate: '2025-01-01', endDate: '2025-01-14', state: 'completed' },
        { id: '2', name: 'Sprint 2', projectId: '1', startDate: '2025-01-15', endDate: '2025-01-28', state: 'active' },
        { id: '3', name: 'Sprint 3', projectId: '2', startDate: '2025-02-01', endDate: '2025-02-14', state: 'planning' }
      ] : sprints;

      const fallbackProjects = projects.length === 0 ? [
        { id: '1', name: 'Project Alpha', startDate: '2025-01-01', endDate: '2025-06-30', status: 'active' },
        { id: '2', name: 'Project Beta', startDate: '2025-02-01', endDate: '2025-08-31', status: 'active' }
      ] : projects;

      // Generate mock user productivity data
      const mockUserProductivity: UserProductivity[] = fallbackUsers.map(user => ({
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        userEmail: user.email,
        userRole: user.role,
        projects: fallbackProjects.slice(0, 2).map(p => p.name), // Mock data
        teams: ['Team Alpha', 'Team Beta'], // Mock data
        totalIssues: Math.floor(Math.random() * 50) + 10,
        completedIssues: Math.floor(Math.random() * 30) + 5,
        inProgressIssues: Math.floor(Math.random() * 10) + 1,
        storyPoints: Math.floor(Math.random() * 100) + 20,
        completedStoryPoints: Math.floor(Math.random() * 60) + 10,
        velocity: Math.floor(Math.random() * 20) + 5,
        averageCycleTime: Math.floor(Math.random() * 10) + 2,
        lastActivity: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
        avatar: user.avatar
      }));

      // Generate mock sprint productivity data
      const mockSprintProductivity: SprintProductivity[] = fallbackSprints.map(sprint => ({
        sprintId: sprint.id,
        sprintName: sprint.name,
        projectName: fallbackProjects.find(p => p.id === sprint.projectId)?.name || 'Unknown Project',
        startDate: sprint.startDate,
        endDate: sprint.endDate,
        totalIssues: Math.floor(Math.random() * 30) + 10,
        completedIssues: Math.floor(Math.random() * 20) + 5,
        inProgressIssues: Math.floor(Math.random() * 8) + 2,
        storyPoints: Math.floor(Math.random() * 50) + 15,
        completedStoryPoints: Math.floor(Math.random() * 30) + 8,
        velocity: Math.floor(Math.random() * 15) + 3,
        burndownRate: Math.random() * 100,
        teamSize: Math.floor(Math.random() * 8) + 3,
        status: sprint.state as 'active' | 'completed' | 'planned',
        progress: Math.random() * 100
      }));

      // Generate mock project productivity data
      const mockProjectProductivity: ProjectProductivity[] = fallbackProjects.map(project => ({
        projectId: project.id,
        projectName: project.name,
        totalSprints: Math.floor(Math.random() * 10) + 3,
        activeSprints: Math.floor(Math.random() * 3) + 1,
        completedSprints: Math.floor(Math.random() * 8) + 2,
        totalIssues: Math.floor(Math.random() * 100) + 30,
        completedIssues: Math.floor(Math.random() * 60) + 15,
        inProgressIssues: Math.floor(Math.random() * 20) + 5,
        storyPoints: Math.floor(Math.random() * 200) + 50,
        completedStoryPoints: Math.floor(Math.random() * 120) + 30,
        velocity: Math.floor(Math.random() * 25) + 8,
        teamSize: Math.floor(Math.random() * 12) + 5,
        startDate: project.startDate,
        endDate: project.endDate,
        status: project.status as 'active' | 'completed' | 'on-hold',
        progress: Math.random() * 100
      }));

      console.log('📊 Generated mock data:', {
        userProductivity: mockUserProductivity.length,
        sprintProductivity: mockSprintProductivity.length,
        projectProductivity: mockProjectProductivity.length
      });

      setUserProductivity(mockUserProductivity);
      setSprintProductivity(mockSprintProductivity);
      setProjectProductivity(mockProjectProductivity);

      // Calculate overall metrics
      const overall: ProductivityMetrics = {
        totalIssues: mockProjectProductivity.reduce((sum, p) => sum + p.totalIssues, 0),
        completedIssues: mockProjectProductivity.reduce((sum, p) => sum + p.completedIssues, 0),
        inProgressIssues: mockProjectProductivity.reduce((sum, p) => sum + p.inProgressIssues, 0),
        overdueIssues: Math.floor(Math.random() * 20) + 5,
        storyPoints: mockProjectProductivity.reduce((sum, p) => sum + p.storyPoints, 0),
        completedStoryPoints: mockProjectProductivity.reduce((sum, p) => sum + p.completedStoryPoints, 0),
        velocity: mockProjectProductivity.reduce((sum, p) => sum + p.velocity, 0) / mockProjectProductivity.length,
        burndownRate: 75.5,
        teamSize: fallbackUsers.length,
        activeUsers: Math.floor(fallbackUsers.length * 0.8),
        averageCycleTime: 5.2,
        leadTime: 8.7
      };

      setOverallMetrics(overall);

    } catch (error) {
      console.error('Error loading productivity metrics:', error);
      setError('Failed to load productivity metrics.');
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return '#4caf50';
    if (progress >= 60) return '#ff9800';
    return '#f44336';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#2196f3';
      case 'completed': return '#4caf50';
      case 'planned': return '#ff9800';
      case 'on-hold': return '#f44336';
      default: return '#757575';
    }
  };

  const getVelocityColor = (velocity: number) => {
    if (velocity >= 15) return '#4caf50';
    if (velocity >= 10) return '#ff9800';
    return '#f44336';
  };

  if (!canViewAnalytics) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to view productivity analytics.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadProductivityData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2c3e50' }}>
          Productivity Analytics
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadProductivityData}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Overall Metrics */}
      {overallMetrics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: '#ffffff', border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 600, mb: 0.5, color: '#2c3e50' }}>
                      {overallMetrics.totalIssues}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#7f8c8d', fontWeight: 500 }}>
                      Total Issues
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#f8f9fa', color: '#495057' }}>
                    <Assignment sx={{ fontSize: 32 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: '#ffffff', border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 600, mb: 0.5, color: '#2c3e50' }}>
                      {overallMetrics.completedIssues}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#7f8c8d', fontWeight: 500 }}>
                      Completed
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#d4edda', color: '#155724' }}>
                    <CheckCircle sx={{ fontSize: 32 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: '#ffffff', border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 600, mb: 0.5, color: '#2c3e50' }}>
                      {overallMetrics.velocity.toFixed(1)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#7f8c8d', fontWeight: 500 }}>
                      Avg Velocity
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#fff3cd', color: '#856404' }}>
                    <TrendingUp sx={{ fontSize: 32 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: '#ffffff', border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 600, mb: 0.5, color: '#2c3e50' }}>
                      {overallMetrics.teamSize}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#7f8c8d', fontWeight: 500 }}>
                      Team Size
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#e9ecef', color: '#495057' }}>
                    <Group sx={{ fontSize: 32 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, background: '#ffffff', border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={selectedProject}
                label="Project"
                onChange={(e) => setSelectedProject(e.target.value)}
              >
                <MenuItem value="all">All Projects</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sprint</InputLabel>
              <Select
                value={selectedSprint}
                label="Sprint"
                onChange={(e) => setSelectedSprint(e.target.value)}
              >
                <MenuItem value="all">All Sprints</MenuItem>
                {sprints.map((sprint) => (
                  <MenuItem key={sprint.id} value={sprint.id}>
                    {sprint.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>User</InputLabel>
              <Select
                value={selectedUser}
                label="User"
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                <MenuItem value="all">All Users</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => setDateRange(e.target.value)}
              >
                <MenuItem value="7days">Last 7 Days</MenuItem>
                <MenuItem value="30days">Last 30 Days</MenuItem>
                <MenuItem value="90days">Last 90 Days</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="1year">Last Year</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card sx={{ background: '#ffffff', border: '1px solid #e0e0e0' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="By Sprint" />
            <Tab label="By Project" />
            <Tab label="By User" />
          </Tabs>
        </Box>

        {/* Sprint Productivity Tab */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Sprint</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Issues</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Story Points</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Velocity</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Team Size</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sprintProductivity.map((sprint) => (
                  <TableRow key={sprint.sprintId} hover>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {sprint.sprintName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>{sprint.projectName}</TableCell>
                    <TableCell>
                      <Chip
                        label={sprint.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(sprint.status),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={sprint.progress}
                          sx={{
                            width: 60,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#e9ecef',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getProgressColor(sprint.progress)
                            }
                          }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          {sprint.progress.toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {sprint.completedIssues}/{sprint.totalIssues}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sprint.inProgressIssues} in progress
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {sprint.completedStoryPoints}/{sprint.storyPoints}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${sprint.velocity} pts/sprint`}
                        size="small"
                        sx={{
                          backgroundColor: getVelocityColor(sprint.velocity),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{sprint.teamSize} members</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Project Productivity Tab */}
        <TabPanel value={tabValue} index={1}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Sprints</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Issues</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Story Points</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Velocity</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Team Size</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projectProductivity.map((project) => (
                  <TableRow key={project.projectId} hover>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {project.projectName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(project.startDate).toLocaleDateString()}
                        {project.endDate && ` - ${new Date(project.endDate).toLocaleDateString()}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={project.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(project.status),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={project.progress}
                          sx={{
                            width: 60,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#e9ecef',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getProgressColor(project.progress)
                            }
                          }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          {project.progress.toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {project.activeSprints} active, {project.completedSprints} completed
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {project.totalSprints} total
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {project.completedIssues}/{project.totalIssues}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {project.inProgressIssues} in progress
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {project.completedStoryPoints}/{project.storyPoints}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${project.velocity.toFixed(1)} pts/sprint`}
                        size="small"
                        sx={{
                          backgroundColor: getVelocityColor(project.velocity),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{project.teamSize} members</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* User Productivity Tab */}
        <TabPanel value={tabValue} index={2}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Projects</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Issues</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Story Points</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Velocity</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Cycle Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userProductivity.map((user) => (
                  <TableRow key={user.userId} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={user.avatar} sx={{ width: 32, height: 32 }}>
                          {user.userName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {user.userName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.userEmail}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.userRole}
                        size="small"
                        sx={{
                          backgroundColor: '#e9ecef',
                          color: '#495057',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {user.projects.slice(0, 2).map((project, index) => (
                          <Chip
                            key={index}
                            label={project}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        ))}
                        {user.projects.length > 2 && (
                          <Typography variant="caption" color="text.secondary">
                            +{user.projects.length - 2} more
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.completedIssues}/{user.totalIssues}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.inProgressIssues} in progress
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.completedStoryPoints}/{user.storyPoints}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${user.velocity} pts/sprint`}
                        size="small"
                        sx={{
                          backgroundColor: getVelocityColor(user.velocity),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.averageCycleTime} days</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(user.lastActivity).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default ProductivityAnalytics;
